import { useState, useContext } from "react";
import Axios from "axios";
import Title from "./component/Title";
import GlobalContext from "./GlobalContext";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";

function Login() {
  const { globalURL } = useContext(GlobalContext);
  const url = globalURL;
  const navigate = useNavigate();

  // "player" = passwordless SMS code; "staff" = email + password.
  const [mode, setMode] = useState("player");

  Axios.defaults.withCredentials = true;

  const storeSessionAndRoute = (data) => {
    localStorage.setItem("token", data.token);
    localStorage.setItem("refresh", data.refreshToken);
    if (data.user && data.user.role === "player") {
      return navigate("/account");
    }
    return navigate("/admin");
  };

  return (
    <div>
      <div className="container">
        <div className="bgRed sm:w-[85%] lg:w-[60%] relative flex justify-center flex-col items-center p-4 border-black border-8 rounded-3xl my-8 sm-shadow">
          <Title word="Login" />
        </div>
        <div className="content my-8 justify-start items-center sm:w-[95%] lg:w-[80%] sm:pl-4 sm:pr-4 lg:pl-10 lg:pr-10 pt-10 pb-10 black-bg border-black border-8">
          <div className="max-w-[320px] w-full">
            {mode === "player" ? (
              <PlayerLogin url={url} onSuccess={storeSessionAndRoute} />
            ) : (
              <StaffLogin url={url} onSuccess={storeSessionAndRoute} />
            )}

            <div className="mt-6 flex flex-col gap-1">
              {mode === "player" ? (
                <>
                  <Button variant="text" onClick={() => navigate("/register")}>
                    New player? Create an account
                  </Button>
                  <Button variant="text" onClick={() => setMode("staff")}>
                    Staff login
                  </Button>
                </>
              ) : (
                <Button variant="text" onClick={() => setMode("player")}>
                  Player login
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlayerLogin({ url, onSuccess }) {
  const [step, setStep] = useState("phone"); // "phone" | "code"
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = (e) => {
    e.preventDefault();
    setErr("");
    setInfo("");
    setLoading(true);
    Axios.post(url + "/users/request-otp", { phone })
      .then(() => {
        setInfo("If that number has an account, a code was sent.");
        setStep("code");
      })
      .catch((error) => {
        setErr(
          typeof error?.response?.data === "string"
            ? error.response.data
            : "Could not send a code."
        );
      })
      .finally(() => setLoading(false));
  };

  const verifyCode = (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    Axios.post(url + "/users/verify-otp", { phone, code })
      .then((res) => onSuccess(res.data))
      .catch((error) => {
        setErr(
          typeof error?.response?.data === "string"
            ? error.response.data
            : "Incorrect code."
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="flex flex-col gap-4">
      <h1>Player Login</h1>
      {step === "phone" ? (
        <form onSubmit={requestCode} className="flex flex-col gap-3">
          <FormControl>
            <InputLabel id="phone-label">Mobile Phone</InputLabel>
            <OutlinedInput
              label="Mobile Phone"
              type="tel"
              required
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </FormControl>
          {err && <strong className="text-red-500 text-center">{err}</strong>}
          <Button variant="outlined" type="submit" disabled={loading}>
            {loading ? "Sending..." : "Text me a code"}
          </Button>
        </form>
      ) : (
        <form onSubmit={verifyCode} className="flex flex-col gap-3">
          {info && <span className="text-green-400 text-sm">{info}</span>}
          <FormControl>
            <InputLabel id="code-label">6-digit code</InputLabel>
            <OutlinedInput
              label="6-digit code"
              type="text"
              inputProps={{ inputMode: "numeric", maxLength: 6 }}
              required
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
          </FormControl>
          {err && <strong className="text-red-500 text-center">{err}</strong>}
          <Button variant="outlined" type="submit" disabled={loading}>
            {loading ? "Verifying..." : "Sign In"}
          </Button>
          <Button
            variant="text"
            type="button"
            onClick={() => {
              setStep("phone");
              setCode("");
              setErr("");
            }}
          >
            Use a different number
          </Button>
        </form>
      )}
    </div>
  );
}

function StaffLogin({ url, onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const login = (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    Axios.post(url + "/users/login", { email, password })
      .then((res) => onSuccess(res.data))
      .catch(() => setErr("Email or password is wrong"))
      .finally(() => setLoading(false));
  };

  return (
    <div className="flex flex-col gap-4">
      <h1>Staff Login</h1>
      <form onSubmit={login} className="flex flex-col gap-2">
        <FormControl>
          <InputLabel id="email-label">Email</InputLabel>
          <OutlinedInput
            label="Email"
            name="email"
            type="text"
            required
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormControl>
        <FormControl>
          <InputLabel id="password-label">Password</InputLabel>
          <OutlinedInput
            label="Password"
            type="password"
            name="password"
            required
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </FormControl>
        {err && <strong className="text-red-500 text-center">{err}</strong>}
        <Button variant="outlined" type="submit" disabled={loading}>
          {loading ? "Signing in..." : "Sign In"}
        </Button>
      </form>
    </div>
  );
}

export default Login;
